import * as React from "react";
import { useSelector } from "react-redux";
import { portfoliosAuditEvents } from "~/AIEvents/portfoliosAuditEvents";
import { useTrackEvent } from "~/common/audit";
import { calculateMaxLength } from "~/EasyProjects/metadataHelper";
import { metadata } from "~/EasyProjectsCommon/EntitiesMetadata";
import { getPortfolio, isPortfoliosDataLoaded, loadPortfolios } from "~/Redux/portfolios";
import { addPortfolio } from "~/Redux/portfolios";
import { AddPortfolioPayload } from "~/Redux/portfolios/slice";
import { getUserPermissions } from "~/Redux/UserPermissions";
import { useReduxAction } from "../hooks/useReduxAction";
import { getItemLabel } from "../list";
import { EntityPathItem } from "../listRows/EntityPathRow";
import { useRenderModal } from "../modalPopup";
import { InlineCreatableProps, InlinePopupSelectVirtProps } from "../select";
import { CreatePortfolioModal, CreatePortfolioModalProps } from "./CreatePortfolioModal";
import { PortfolioRow } from "./PortfolioRow";
import { getIsNestedPortfoliosEnabled } from "./selectors";

type CommonProps = Pick<
    InlinePopupSelectVirtProps<EntityPathItem>,
    "listRowComponent" | "inputMaxLength" | "loadOptions" | "isLoading" | "canCreate" | "getItemLabel"
> &
    Pick<InlineCreatableProps<EntityPathItem, number>, "createAction">;

type Args = {
    selectedId: number;
    excludedPortfolioId?: number;
};

export const usePortfolioListProps = ({ selectedId, excludedPortfolioId }: Args): CommonProps => {
    const trackEvent = useTrackEvent();

    const createPortfolioAction = useReduxAction(addPortfolio);
    const createPortfolio = ((payload: AddPortfolioPayload) => {
        trackEvent(portfoliosAuditEvents.create);
        return createPortfolioAction(payload);
    }) as typeof addPortfolio;

    const loadOptions = useReduxAction(() => loadPortfolios({ forceReload: true }));
    const inputMaxLength = calculateMaxLength(metadata.Portfolio.NameMaxLength);

    const renderModal = useRenderModal<
        Pick<CreatePortfolioModalProps, "name" | "callback"> & { cancelCreating(): void }
    >((close, { cancelCreating, ...props }) => (
        <CreatePortfolioModal
            excludedPortfolioId={excludedPortfolioId}
            inputMaxLength={inputMaxLength}
            createPortfolio={createPortfolio}
            closeModal={() => {
                cancelCreating();
                close();
            }}
            {...props}
        />
    ));

    const isPortfoliosLoaded = useSelector(isPortfoliosDataLoaded);
    const selectedPortfolio = useSelector(getPortfolio(selectedId));
    const selectedExists = !selectedId || !!selectedPortfolio;
    const permissions = useSelector(getUserPermissions);
    const isNestedPortfoliosEnabled = useSelector(getIsNestedPortfoliosEnabled);

    const isLoaded = isPortfoliosLoaded && selectedExists;

    const createAction: CommonProps["createAction"] = (name, callback, cancelCreating) => {
        if (isNestedPortfoliosEnabled) {
            renderModal({ name, callback, cancelCreating });
        } else {
            createPortfolio({ name, callback });
        }
    };

    return {
        listRowComponent: PortfolioRow,
        inputMaxLength,
        loadOptions,
        isLoading: !isLoaded,
        canCreate: permissions?.CanAddPortfolios,
        createAction,
        getItemLabel,
    };
};
